// Mock data for Botverse Web

export const navLinks = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Products", path: "/products" },
  { name: "Academy", path: "/academy" },
  { name: "About", path: "/about" },
  { name: "Consultation", path: "/consultation" },
  { name: "Contact", path: "/contact" }
];

export const heroData = {
  headline: "Where AI, Automation, and Human Expertise Converge",
  subheadline: "We empower businesses with cutting-edge AI solutions, build intelligent products, and develop the next generation of tech talent worldwide.",
  primaryCta: "Book a Consultation",
  secondaryCta: "Explore Academy"
};

export const pillars = [
  {
    id: 1,
    title: "AI & Automation Consulting",
    description: "Strategic AI implementation and workflow automation that transforms how your business operates.",
    icon: "Brain"
  },
  {
    id: 2,
    title: "Product Development",
    description: "Custom software and AI-powered tools built to solve real business challenges at scale.",
    icon: "Code"
  },
  {
    id: 3,
    title: "Cybersecurity Solutions",
    description: "Protect your digital assets with AI-powered security, threat detection, and compliance solutions.",
    icon: "Shield"
  },
  {
    id: 4,
    title: "Education & Training",
    description: "Professional development programs that build practical, job-ready AI and tech skills.",
    icon: "GraduationCap"
  }
];

export const audiences = [
  {
    id: 1,
    title: "Startups",
    description: "Launch faster with AI-powered MVPs and automation from day one.",
    icon: "Rocket"
  },
  {
    id: 2,
    title: "SMEs",
    description: "Scale operations efficiently with intelligent automation solutions.",
    icon: "Building"
  },
  {
    id: 3,
    title: "Enterprises",
    description: "Transform legacy systems with enterprise-grade AI integration.",
    icon: "Building2"
  },
  {
    id: 4,
    title: "Professionals",
    description: "Advance your career with practical AI skills and certifications.",
    icon: "User"
  }
];

export const whyUs = [
  {
    id: 1,
    title: "Global Presence",
    description: "Operating across US, Europe, Middle East, Africa, and Asia."
  },
  {
    id: 2,
    title: "Worldwide Reach",
    description: "Connecting talent and businesses across continents with seamless collaboration."
  },
  {
    id: 3,
    title: "Practical, Applied AI",
    description: "Real solutions that work, not theoretical concepts."
  },
  {
    id: 4,
    title: "Industry-Aligned",
    description: "Solutions built for the specific needs of each industry."
  }
];

export const useCases = [
  {
    id: 1,
    title: "Business Automation",
    description: "Reduced manual processes by 70% for a logistics company",
    metric: "70%",
    metricLabel: "Process Reduction"
  },
  {
    id: 2,
    title: "AI-Powered Workflows",
    description: "Implemented intelligent document processing for a financial services firm",
    metric: "5x",
    metricLabel: "Faster Processing"
  },
  {
    id: 3,
    title: "Team Upskilling",
    description: "Trained 200+ professionals in AI fundamentals across 3 enterprises",
    metric: "200+",
    metricLabel: "Professionals Trained"
  }
];

export const services = {
  consulting: {
    title: "AI & Automation Consulting",
    description: "Strategic guidance to help you leverage AI effectively",
    items: [
      {
        title: "AI Strategy Development",
        description: "Comprehensive roadmaps for AI adoption aligned with your business goals"
      },
      {
        title: "Workflow Automation",
        description: "Streamline repetitive tasks and optimize operational efficiency"
      },
      {
        title: "Process Optimization",
        description: "Identify bottlenecks and implement AI-driven improvements"
      },
      {
        title: "Custom AI Solutions",
        description: "Bespoke AI implementations tailored to your unique challenges"
      }
    ]
  },
  development: {
    title: "Software & Product Development",
    description: "Build intelligent solutions that drive results",
    items: [
      {
        title: "AI-Powered Tools",
        description: "Custom applications enhanced with machine learning capabilities"
      },
      {
        title: "Internal Business Systems",
        description: "ERPs, CRMs, and internal tools built for your workflow"
      },
      {
        title: "MVPs for Startups",
        description: "Rapid prototyping and development to validate your ideas"
      },
      {
        title: "API & System Integrations",
        description: "Seamlessly connect your tools and data sources"
      }
    ]
  },
  cybersecurity: {
    title: "Cybersecurity Solutions",
    description: "Protect your business with advanced security measures",
    items: [
      {
        title: "AI-Powered Threat Detection",
        description: "Identify and neutralize threats before they impact your business"
      },
      {
        title: "Security Audits & Assessments",
        description: "Comprehensive evaluation of your security posture and vulnerabilities"
      },
      {
        title: "Compliance & Governance",
        description: "Ensure adherence to industry regulations and data protection standards"
      },
      {
        title: "Incident Response Planning",
        description: "Prepare your organization to respond effectively to security incidents"
      }
    ]
  },
  training: {
    title: "Corporate Training & Upskilling",
    description: "Develop AI-ready teams and future-proof your workforce",
    items: [
      {
        title: "Team Training Programs",
        description: "Customized workshops for technical and non-technical teams"
      },
      {
        title: "AI Literacy Programs",
        description: "Foundation courses that demystify AI for everyone"
      },
      {
        title: "Custom Bootcamps",
        description: "Intensive programs designed around your specific needs"
      },
      {
        title: "Executive Briefings",
        description: "Strategic AI insights for leadership and decision-makers"
      }
    ]
  }
};

export const academyData = {
  headline: "Botverse Academy",
  subheadline: "Professional training and bootcamps that build practical, job-ready skills for the AI era.",
  features: [
    {
      title: "Live Cohorts",
      description: "Learn alongside peers in structured, instructor-led programs"
    },
    {
      title: "Industry Certifications",
      description: "Earn credentials recognized by employers globally"
    },
    {
      title: "Mentorship",
      description: "Get guidance from experienced professionals in the field"
    },
    {
      title: "Practical Projects",
      description: "Build a portfolio with real-world applications"
    }
  ],
  tracks: [
    "AI & Machine Learning",
    "Data Science & Analytics",
    "Software Engineering",
    "Cybersecurity",
    "Product Management",
    "No-Code AI Tools"
  ]
};

export const products = [
  {
    id: 1,
    name: "AI Workflow Engine",
    description: "Automate complex business processes with our intelligent workflow automation platform.",
    status: "available",
    category: "Automation Tools"
  },
  {
    id: 2,
    name: "DocuSmart AI",
    description: "Intelligent document processing and extraction powered by advanced NLP.",
    status: "available",
    category: "AI Assistants"
  },
  {
    id: 3,
    name: "TeamPulse",
    description: "AI-driven productivity analytics and team performance insights.",
    status: "coming-soon",
    category: "Productivity Platforms"
  },
  {
    id: 4,
    name: "DataBridge",
    description: "Unified data integration platform with AI-powered transformations.",
    status: "coming-soon",
    category: "Automation Tools"
  }
];

export const aboutData = {
  mission: "To empower businesses and individuals with practical AI solutions that drive real-world impact.",
  vision: "A world where talent from every continent leads global innovation, and AI serves as a force for inclusive growth.",
  values: [
    {
      title: "Practical Impact",
      description: "We build solutions that work in the real world, not just in theory."
    },
    {
      title: "Ethics & Responsibility",
      description: "We develop AI with integrity, transparency, and human welfare in mind."
    },
    {
      title: "Continuous Learning",
      description: "We stay at the forefront of technology and share our knowledge generously."
    },
    {
      title: "Inclusive Excellence",
      description: "We believe great talent exists everywhere and deserves equal opportunity."
    }
  ],
  story: "Founded with a vision to bridge the gap between cutting-edge AI technology and practical business applications, Botverse Web has grown into a global technology company operating across multiple continents. We combine deep technical expertise with a commitment to education and capacity building worldwide."
};

export const consultationData = {
  headline: "Let's Build Something Extraordinary",
  subheadline: "Schedule a consultation to discuss how AI and automation can transform your business.",
  process: [
    {
      step: 1,
      title: "Discovery Call",
      description: "We learn about your business, challenges, and goals."
    },
    {
      step: 2,
      title: "Assessment",
      description: "Our team analyzes opportunities for AI and automation."
    },
    {
      step: 3,
      title: "Proposal",
      description: "We present a customized solution with clear outcomes."
    },
    {
      step: 4,
      title: "Implementation",
      description: "We execute with precision, keeping you informed every step."
    }
  ],
  benefits: [
    "Free initial consultation",
    "No-obligation assessment",
    "Expert guidance from day one",
    "Clear pricing and timelines"
  ]
};

export const contactData = {
  email: "support@botverseweb.com",
  regions: [
    "America",
    "Europe",
    "Middle East",
    "Africa",
    "Asia"
  ]
};

export const footerData = {
  tagline: "Where AI, Automation, and Human Expertise Converge",
  sections: [
    {
      title: "Company",
      links: [
        { name: "About", path: "/about" },
        { name: "Services", path: "/services" },
        { name: "Products", path: "/products" },
        { name: "Contact", path: "/contact" }
      ]
    },
    {
      title: "Services",
      links: [
        { name: "AI Consulting", path: "/services" },
        { name: "Product Development", path: "/services" },
        { name: "Cybersecurity", path: "/services" },
        { name: "Corporate Training", path: "/services" }
      ]
    },
    {
      title: "Resources",
      links: [
        { name: "Consultation", path: "/consultation" },
        { name: "Academy", path: "/academy" },
        { name: "Contact", path: "/contact" }
      ]
    }
  ]
};
